import { TokenManager } from '../utils/storage/tokenmanager.storage';
import { apiService } from '../utils/api/api.service';
import { API_CONFIG } from '../utils/apiConfig';

const UPLOAD_REQUEST_CONFIG = { timeout: API_CONFIG.UPLOAD_TIMEOUT };

class GCPStorageService {
  /**
   * POST multipart upload data with the extended upload timeout, retrying
   * transient failures (network drop / timeout / 5xx) once before giving up.
   * 4xx responses are never retried.
   */
  private async postUpload<T>(
    url: string,
    data: FormData,
  ): Promise<T> {
    const maxAttempts = 2;
    let lastError: unknown;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await apiService.post<T>(url, data, UPLOAD_REQUEST_CONFIG);
        return response.data;
      } catch (error: any) {
        lastError = error;
        const status: number | undefined = error?.statusCode ?? error?.response?.status;
        const isTransient = !status || status >= 500;
        if (!isTransient || attempt === maxAttempts - 1) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }

    throw lastError;
  }

  async getPresignedUrlForProfile(file: File, userId: string) {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const response = await apiService.post<any>("/upload/profile", {
      fileName: file.name,
      fileType: file.type,
      userId,
    });

    return response.data;
  }

  async getPresignedUrlForChat(file: File, userId: string) {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const response = await apiService.post<any>("/upload/chat", {
      fileName: file.name,
      fileType: file.type,
      userId,
    });

    return response.data;
  }

  async getPresignedUrlForProduct(file: File, userId: string) {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const response = await apiService.post<any>("/upload/product", {
      fileName: file.name,
      fileType: file.type,
      userId,
    });

    return response.data;
  }

  /**
   * Upload product image via backend proxy (no direct PUT to GCS = no CORS).
   * Use this instead of getPresignedUrlForProduct + uploadFile for product images.
   */
  async uploadProductImageViaBackend(
    file: File,
    userId: string
  ): Promise<{ viewUrl: string; key: string }> {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const formData = new FormData();
    formData.append("file", file);

    const data = await this.postUpload<any>("/upload/product/file", formData);

    const viewUrl = data.viewUrl ?? data.fileUrl ?? data.data?.viewUrl;
    const key = data.key ?? data.data?.key ?? "";
    if (!viewUrl) throw new Error("Upload response missing viewUrl");
    return { viewUrl, key };
  }

  /**
   * Upload request image via backend proxy (no direct PUT to GCS = no CORS).
   */
  async uploadRequestImageViaBackend(
    file: File,
    userId: string
  ): Promise<{ viewUrl: string; key: string }> {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const formData = new FormData();
    formData.append("file", file);

    const data = await this.postUpload<any>("/upload/request/file", formData);

    const viewUrl = data.viewUrl ?? data.fileUrl ?? data.data?.viewUrl;
    const key = data.key ?? data.data?.key ?? "";
    if (!viewUrl) throw new Error("Upload response missing viewUrl");
    return { viewUrl, key };
  }

  async uploadFile(file: File, uploadUrl: string): Promise<boolean> {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload failed:", {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries((response.headers as any).entries()),
          errorText,
        });
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`,
        );
      }

      return true;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  }

  async getViewUrl(fileUrl: string): Promise<string> {
    return fileUrl;
  }

  async deleteFile(fileUrl: string): Promise<boolean> {
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("User not authenticated");

    const response = await apiService.delete<any>("/upload", {
      data: { fileUrl },
    });

    return !!response.data || response.status >= 200 && response.status < 300;
  }
}

export const gcpStorageService = new GCPStorageService();
